import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '@modules/auth/services/auth.service';
import { Destroyable } from '@shared/utils';
import { SidebarMenuItem } from '../models';
import { SidebarMenuService } from '../services';

@Component({
  standalone: true,
  selector: 'app-dashboard-sidebar',
  templateUrl: './dashboard-sidebar.component.html',
  styleUrl: './dashboard-sidebar.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    TranslocoModule,
  ],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class DashboardSidebarComponent extends Destroyable implements OnInit {
  @Input() isMobile = false;
  @Input() collapsed = false;
  @Input() hideLogoSection = false;
  @Output() menuItemClick = new EventEmitter<void>();
  @Output() collapseToggle = new EventEmitter<void>();

  private router = inject(Router);
  private authService = inject(AuthService);
  private sidebarMenuService = inject(SidebarMenuService);

  loading = signal(false);
  userMenuExpanded = false;
  menuItems: SidebarMenuItem[] = [];
  /** Fallback na ogólny podtytuł, dopóki nie znamy typu konta użytkownika */
  brandSubtitleKey = 'layout.sidebar.brandSubtitle';

  private currentAccountType: 'candidate' | 'company' = 'candidate';

  userInfo = {
    name: '',
    status: 'Premium',
    avatar: ''
  };

  constructor() {
    super();
    this.menuItems = this.sidebarMenuService.getStaticMenu();
    this.authService.currentUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(user => {
        if (user) {
          this.userInfo = {
            name: user.fullName,
            status: 'Premium',
            avatar: user.initials
          };
          this.currentAccountType = user.accountType === 'company' ? 'company' : 'candidate';
          this.brandSubtitleKey = this.currentAccountType === 'company'
            ? 'layout.sidebar.brandSubtitleCompany'
            : 'layout.sidebar.brandSubtitleCandidate';
          this.menuItems = this.sidebarMenuService.getStaticMenu(this.currentAccountType);
          this.updateActiveStateFromRoute(this.router.url);
        }
      });
  }

  ngOnInit(): void {
    this.setupRouteListener();
  }

  private setupRouteListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveStateFromRoute(event.urlAfterRedirects);
      });
  }

  onMenuItemClick(item: SidebarMenuItem): void {
    if (item.children?.length && item.route) {
      // Item with children (e.g. "Moje CV") — expand and navigate to first child
      item.expanded = true;
      const firstChild = this.findFirstRoutableChild(item.children);
      if (firstChild?.route) {
        this.router.navigate([firstChild.route]);
        this.clearActiveStates();
        firstChild.active = true;
      }
    } else if (item.route) {
      this.router.navigate([item.route]);
      this.clearActiveStates();
      item.active = true;
    }

    if (item.command) {
      item.command();
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  private findFirstRoutableChild(items: SidebarMenuItem[]): SidebarMenuItem | null {
    for (const item of items) {
      if (item.route && !item.isCategory) {
        return item;
      }
      if (item.children?.length) {
        const found = this.findFirstRoutableChild(item.children);
        if (found) return found;
      }
    }
    return null;
  }

  toggleCategory(item: SidebarMenuItem): void {
    item.expanded = !item.expanded;
  }

  toggleExpand(item: SidebarMenuItem, event: Event): void {
    event.stopPropagation();
    item.expanded = !item.expanded;
  }

  onNewAcademy(): void {
    console.log('Tworzenie nowej akademii...');
  }

  onCollapseToggle(): void {
    this.collapseToggle.emit();
  }

  toggleUserMenu(): void {
    this.userMenuExpanded = !this.userMenuExpanded;
  }

  onUserMenuAction(action: 'profile' | 'settings' | 'logout'): void {
    this.userMenuExpanded = false;

    switch (action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'logout':
        this.authService.logout();
        break;
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  private clearActiveStates(): void {
    const clearRecursive = (items: SidebarMenuItem[]) => {
      items.forEach(item => {
        item.active = false;
        if (item.children?.length) {
          clearRecursive(item.children);
        }
      });
    };
    clearRecursive(this.menuItems);
  }

  private updateActiveStateFromRoute(url: string): void {
    this.clearActiveStates();

    const findAndActivate = (items: SidebarMenuItem[], parents: SidebarMenuItem[] = []): boolean => {
      for (const item of items) {
        // For category items, traverse children but don't treat category itself as matchable
        if (item.isCategory && item.children?.length) {
          const childFound = findAndActivate(item.children, parents);
          if (childFound) return true;
          continue;
        }

        if (item.children?.length) {
          const childFound = findAndActivate(item.children, [...parents, item]);
          if (childFound) {
            return true;
          }
        }

        const isMatch = item.route && (
          url === item.route ||
          url.startsWith(item.route + '/')
        );

        if (isMatch) {
          item.active = true;
          parents.forEach(parent => parent.expanded = true);

          if (item.children?.length) {
            item.expanded = true;
          }
          return true;
        }
      }
      return false;
    };

    findAndActivate(this.menuItems);
  }
}
